// components/organisation/TeamManagementSection.tsx
'use client';

import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { Trash2, UserMinus, UserPlus, Users } from '@/components/ui/lucide-memo';
import MembersList from '@/components/MembersList';
import SimpleLoader from '@/components/ui/SimpleLoader';
import TeamConsumptionTimeline from '@/components/TeamConsumptionTimeline';
import { formatCreditsInteger } from '#lib/formatCredits';
import { getSpecificErrorMessage } from '#lib/utils';
import { isAuthorizedForOrganisation } from '#lib/organisation-teams/team-access';
import {
  canCreateTeam as canCreateTeamRule,
  canSetTeamReservedCredits,
  getMaxReservableCredits,
  getRequiredSourceTopUp,
  getTeamRemainingCredits,
  getUnassignedMembers,
  readTeamCreditPool,
} from '#lib/organisation-teams/team-rules';
import type {
  MemberData,
  OrganisationTeamDTO,
  OrganisationTeamDetailsDTO,
  OrganisationTeamPlanOptionDTO,
  QuickMenuAction,
} from '#types/types';

interface TeamPlanFieldsetProps {
  legend: string;
  t: TeamManagementTranslations;
  selectId: string;
  creditsInputId: string;
  overageId: string;
  planId: string;
  onPlanChange: (value: string) => void;
  creditsInput: string;
  onCreditsChange: (value: string) => void;
  allowOverage: boolean;
  onAllowOverageChange: (value: boolean) => void;
  autoTopUpSourcePlan: boolean;
  onAutoTopUpSourcePlanChange: (value: boolean) => void;
  plans: OrganisationTeamPlanOptionDTO[];
  currentTeamSourcePlanId?: string | null;
  currentTeamReservedCredits?: number | null;
  currentTeamUsedCredits?: number | null;
}

interface TeamMembersPickerProps {
  legend: string;
  members: MemberData[];
  selectedMemberIds: Set<string>;
  onToggleMemberId: (memberId: string) => void;
  emptyMessage: string;
}

interface TeamManagementSectionProps {
  organisationId: string;
  teams: OrganisationTeamDTO[];
  members: MemberData[];
  isViewerAdmin: boolean;
  currentUserId?: string | null;
  avatarTimestamp?: number;
  onAddMember: () => void;
  onTeamsChanged: () => Promise<void> | void;
  buildMemberContextActions?: (member: MemberData) => QuickMenuAction[];
  canOpenContextMenuFor?: (member: MemberData) => boolean;
}

// ChatGPT's settings controls, as on the organisation's other sections: a filled #212121 pill for
// an action, the white pill for the one primary action, round inputs. The [&]:text-[15px] form
// outweighs the global 12px clamp on button text without an inline style at every call site.
const inputClass =
  'h-10 w-full rounded-full border border-[#2F2F2F] bg-transparent px-4 text-[15px] text-[#ECECEC] placeholder:text-[#8E8E8E] outline-none transition-colors focus:border-[#8E8E8E] disabled:cursor-not-allowed disabled:opacity-45';

const buttonClass =
  'inline-flex h-9 items-center justify-center gap-2 rounded-full bg-[#212121] px-4 !text-[15px] text-[#ECECEC] transition-colors hover:bg-[#2F2F2F] disabled:cursor-not-allowed disabled:opacity-45';

const primaryButtonClass =
  'inline-flex h-9 items-center justify-center gap-2 rounded-full bg-[#F0F0F0] px-4 !text-[15px] font-medium text-black transition-colors hover:bg-white disabled:cursor-not-allowed disabled:opacity-40';

const translations = {
  en: {
    sourcePlan: 'Source plan',
    selectPlan: 'Select plan',
    expiresOn: (date: string) => `expires ${date}`,
    noExpiry: 'no expiry',
    availableCredits: 'Available credits',
    includedCredits: 'Included',
    usedCredits: 'Used',
    reservedCredits: 'Reserved credits',
    reserved: 'Reserved',
    remaining: 'Remaining',
    teamRemainingAfterSave: 'Team remaining after save',
    allowOverageToOrg: 'Allow overage to organisation credits',
    autoTopUpSourcePlan: 'Auto top-up source plan if needed',
    autoTopUpSourcePlanDesc: 'If target budget exceeds source availability, increase source plan credits automatically.',
    requiredSourceTopUp: (value: string) => `Required source top-up: ${value}`,
    failedLoadPlans: 'Failed to load plans',
    failedLoadTeamDetails: 'Failed to load team details',
    planRequired: 'Select a plan to continue.',
    reservedCreditsInvalid: 'Enter a valid credit amount.',
    teamCreated: 'Team created.',
    failedCreateTeam: 'Failed to create team',
    membersAdded: 'Members added.',
    failedAddMembers: 'Failed to add members',
    removeMemberConfirm: 'Remove this member from the team?',
    memberRemoved: 'Member removed from team.',
    failedRemoveMember: 'Failed to remove member',
    teamBudgetUpdated: 'Team budget updated.',
    failedUpdateTeamBudget: 'Failed to update team budget',
    disbandConfirm: 'Disband this team? This cannot be undone.',
    teamDisbanded: 'Team disbanded.',
    failedDisbandTeam: 'Failed to disband team',
    removeFromTeam: 'Remove from team',
    creditsLeft: (value: string) => `${value} credits left`,
    ungrouped: 'Ungrouped',
    noMembersInTeam: 'No members in this team.',
    noUngroupedMembers: 'No ungrouped members.',
    backToOrgMembers: 'Back to organisation members',
    loadingTeamDetails: 'Loading team details...',
    teamDetailsUnavailable: 'Team details unavailable.',
    membersCount: (count: number) => `${count.toLocaleString()} member${count === 1 ? '' : 's'}`,
    credits: 'Credits',
    stats: 'Stats',
    projects: 'Projects',
    generations: 'Generations',
    totalCreditsUsed: 'Total credits used',
    last30Days: 'Last 30 days',
    expires: 'Expires',
    members: 'Members',
    addMembers: 'Add members',
    teamBudget: 'Team budget',
    disbandTeam: 'Disband team',
    addMembersTitle: 'Add members to team',
    cancel: 'Cancel',
    addSelected: 'Add selected',
    saveChanges: 'Save changes',
    addMember: 'Add member',
    addTeam: 'Add team',
    createTeam: 'Create team',
    team: 'Team',
    teamName: 'Team name',
    description: 'Description',
    initialMembers: 'Initial members',
    noMembersAvailable: 'No members available.',
    create: 'Create',
  },
  fr: {
    sourcePlan: 'Plan source',
    selectPlan: 'Choisir un plan',
    expiresOn: (date: string) => `expire le ${date}`,
    noExpiry: 'sans expiration',
    availableCredits: 'Credits disponibles',
    includedCredits: 'Inclus',
    usedCredits: 'Utilises',
    reservedCredits: 'Credits reserves',
    reserved: 'Reserves',
    remaining: 'Restants',
    teamRemainingAfterSave: 'Credits restants apres enregistrement',
    allowOverageToOrg: 'Autoriser le depassement sur les credits org',
    autoTopUpSourcePlan: 'Top-up automatique du plan source si necessaire',
    autoTopUpSourcePlanDesc: 'Si le budget cible depasse la disponibilite du plan source, augmenter automatiquement les credits du plan source.',
    requiredSourceTopUp: (value: string) => `Top-up source requis : ${value}`,
    failedLoadPlans: 'Echec du chargement des plans',
    failedLoadTeamDetails: 'Echec du chargement des details de l\'equipe',
    planRequired: 'Choisissez un plan pour continuer.',
    reservedCreditsInvalid: 'Entrez un montant valide.',
    teamCreated: 'Equipe creee.',
    failedCreateTeam: 'Echec de la creation de l\'equipe',
    membersAdded: 'Membres ajoutes.',
    failedAddMembers: 'Echec de l\'ajout des membres',
    removeMemberConfirm: 'Retirer ce membre de l\'equipe ?',
    memberRemoved: 'Membre retire de l\'equipe.',
    failedRemoveMember: 'Echec du retrait du membre',
    teamBudgetUpdated: 'Budget de l\'equipe mis a jour.',
    failedUpdateTeamBudget: 'Echec de la mise a jour du budget',
    disbandConfirm: 'Dissoudre cette equipe ? Cette action est definitive.',
    teamDisbanded: 'Equipe dissoute.',
    failedDisbandTeam: 'Echec de la dissolution de l\'equipe',
    removeFromTeam: 'Retirer de l\'equipe',
    creditsLeft: (value: string) => `${value} credits restants`,
    ungrouped: 'Sans equipe',
    noMembersInTeam: 'Aucun membre dans cette equipe.',
    noUngroupedMembers: 'Aucun membre sans equipe.',
    backToOrgMembers: 'Retour aux membres de l\'organisation',
    loadingTeamDetails: 'Chargement des details de l\'equipe...',
    teamDetailsUnavailable: 'Details de l\'equipe indisponibles.',
    membersCount: (count: number) => `${count.toLocaleString()} membre${count === 1 ? '' : 's'}`,
    credits: 'Credits',
    stats: 'Statistiques',
    projects: 'Projets',
    generations: 'Generations',
    totalCreditsUsed: 'Credits utilises au total',
    last30Days: '30 derniers jours',
    expires: 'Expiration',
    members: 'Membres',
    addMembers: 'Ajouter des membres',
    teamBudget: 'Budget de l\'equipe',
    disbandTeam: 'Dissoudre l\'equipe',
    addMembersTitle: 'Ajouter des membres a l\'equipe',
    cancel: 'Annuler',
    addSelected: 'Ajouter',
    saveChanges: 'Enregistrer',
    addMember: 'Ajouter un membre',
    addTeam: 'Ajouter une equipe',
    createTeam: 'Creer une equipe',
    team: 'Equipe',
    teamName: 'Nom de l\'equipe',
    description: 'Description',
    initialMembers: 'Membres initiaux',
    noMembersAvailable: 'Aucun membre disponible.',
    create: 'Creer',
  },
};

type TeamManagementTranslations = typeof translations.en;

const formatAccessScope = (value: string): string => {
  if (value === 'CHEAP_MODELS_ONLY') return 'Cost-efficient models only';
  if (value === 'ALL_MODELS') return 'All models';
  return value.replace(/_/g, ' ').toLowerCase();
};

const TeamPlanFieldset = memo(({
  legend,
  t,
  selectId,
  creditsInputId,
  overageId,
  planId,
  onPlanChange,
  creditsInput,
  onCreditsChange,
  allowOverage,
  onAllowOverageChange,
  autoTopUpSourcePlan,
  onAutoTopUpSourcePlanChange,
  plans,
  currentTeamSourcePlanId = null,
  currentTeamReservedCredits = null,
  currentTeamUsedCredits = null,
}: TeamPlanFieldsetProps) => {
  const selectedPlan = plans.find(plan => plan.id === planId) || null;
  const requestedCredits = Number.parseFloat(creditsInput);
  const normalizedRequested = Number.isFinite(requestedCredits) ? requestedCredits : 0;
  const isSamePlan = Boolean(selectedPlan && selectedPlan.id === currentTeamSourcePlanId);
  const maxReservable = selectedPlan
    ? getMaxReservableCredits({
      sourcePlanAvailable: selectedPlan.availableCreditsFloat,
      currentReserved: currentTeamReservedCredits ?? 0,
      isSameSourcePlan: isSamePlan,
    })
    : 0;
  const topUpNeeded = getRequiredSourceTopUp(normalizedRequested, maxReservable);
  const projectedRemaining = Math.max(normalizedRequested - (currentTeamUsedCredits ?? 0), 0);

  return (
    <fieldset className="space-y-4">
      <legend className="mb-2 text-[17px] font-medium text-[#ECECEC]">{legend}</legend>
      <div>
        <label htmlFor={selectId} className="mb-1 block text-sm text-white/70">{t.sourcePlan}</label>
        <select id={selectId} value={planId} onChange={(event) => onPlanChange(event.target.value)} className={inputClass}>
          <option value="">{t.selectPlan}</option>
          {plans.map(plan => {
            const labelParts = [
              formatCreditsInteger(plan.availableCreditsFloat),
              plan.expirationDate ? t.expiresOn(new Date(plan.expirationDate).toLocaleDateString()) : t.noExpiry,
              plan.priceDisplayText,
            ].filter(Boolean);
            return (
              <option key={plan.id} value={plan.id}>
                {labelParts.join(' - ')}
              </option>
            );
          })}
        </select>
        {selectedPlan && (
          <div className="mt-2 space-y-1 text-xs text-white/45">
            <div>{t.availableCredits}: {formatCreditsInteger(selectedPlan.availableCreditsFloat)}</div>
            <div>{t.includedCredits}: {formatCreditsInteger(selectedPlan.includedCreditsFloat)}</div>
            <div>{t.usedCredits}: {formatCreditsInteger(selectedPlan.usedCreditsFloat)}</div>
            <div>{t.reserved}: {formatCreditsInteger(selectedPlan.reservedCreditsFloat)}</div>
            {currentTeamUsedCredits !== null && (
              <div>{t.teamRemainingAfterSave}: {formatCreditsInteger(projectedRemaining)}</div>
            )}
            {selectedPlan.accessScope && <div>{formatAccessScope(selectedPlan.accessScope)}</div>}
          </div>
        )}
      </div>

      <div>
        <label htmlFor={creditsInputId} className="mb-1 block text-sm text-white/70">{t.reservedCredits}</label>
        <input
          id={creditsInputId}
          type="number"
          min="0"
          step="0.01"
          value={creditsInput}
          onChange={(event) => onCreditsChange(event.target.value)}
          className={inputClass}
          placeholder={t.reservedCredits}
          inputMode="decimal"
          disabled={!planId}
        />
      </div>

      <label htmlFor={overageId} className="flex cursor-pointer items-center gap-2 text-sm text-white/70">
        <input
          id={overageId}
          type="checkbox"
          checked={allowOverage}
          onChange={(event) => onAllowOverageChange(event.target.checked)}
          className="h-4 w-4 rounded border-[#2A2A2A] bg-[#1A1A1A] text-[#3B82F6]"
        />
        {t.allowOverageToOrg}
      </label>

      <div className="space-y-1">
        <label htmlFor={`${overageId}-auto-topup`} className="flex cursor-pointer items-center gap-2 text-sm text-white/70">
          <input
            id={`${overageId}-auto-topup`}
            type="checkbox"
            checked={autoTopUpSourcePlan}
            onChange={(event) => onAutoTopUpSourcePlanChange(event.target.checked)}
            className="h-4 w-4 rounded border-[#2A2A2A] bg-[#1A1A1A] text-[#3B82F6]"
          />
          {t.autoTopUpSourcePlan}
        </label>
        <p className="text-xs text-white/40">{t.autoTopUpSourcePlanDesc}</p>
        {topUpNeeded > 0 && (
          <p className="text-xs text-[#FFA344]">{t.requiredSourceTopUp(formatCreditsInteger(topUpNeeded))}</p>
        )}
      </div>
    </fieldset>
  );
});
TeamPlanFieldset.displayName = 'TeamPlanFieldset';

const TeamMembersPicker = memo(({
  legend,
  members,
  selectedMemberIds,
  onToggleMemberId,
  emptyMessage,
}: TeamMembersPickerProps) => (
  <fieldset className="space-y-3">
    <legend className="mb-2 text-[17px] font-medium text-[#ECECEC]">{legend}</legend>
    <div className="max-h-[320px] overflow-y-auto pr-1">
      <MembersList
        members={members}
        isViewerAdmin={false}
        currentUserId={null}
        selectable
        selectedMemberIds={selectedMemberIds}
        onToggleMember={(member) => onToggleMemberId(member.id)}
        emptyMessage={emptyMessage}
      />
    </div>
  </fieldset>
));
TeamMembersPicker.displayName = 'TeamMembersPicker';

export const TeamManagementSection = memo(({
  organisationId,
  teams,
  members,
  isViewerAdmin,
  currentUserId = null,
  avatarTimestamp,
  onAddMember,
  onTeamsChanged,
  buildMemberContextActions,
  canOpenContextMenuFor,
}: TeamManagementSectionProps) => {
  const { kernel } = useKernel();
  const lang = useKernelStore(state => state.language);
  const t = translations[lang];
  const canManageTeams = useKernelStore(state => isAuthorizedForOrganisation({
    isFaterAdmin: state.userContext.isFaterAdmin,
    isOrganisationAdmin: state.userContext.isOrganisationAdmin,
    userOrgId: state.userContext.organisationId,
  }, organisationId));
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [selectedTeamDetails, setSelectedTeamDetails] = useState<OrganisationTeamDetailsDTO | null>(null);
  const [isLoadingTeamDetails, setIsLoadingTeamDetails] = useState(false);
  const [teamDetailsError, setTeamDetailsError] = useState<string | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [descriptionInput, setDescriptionInput] = useState('');
  const [selectedMemberIds, setSelectedMemberIds] = useState<Set<string>>(new Set());
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [reservedCreditsInput, setReservedCreditsInput] = useState('');
  const [allowOverageToOrg, setAllowOverageToOrg] = useState(true);
  const [createAutoTopUpSourcePlan, setCreateAutoTopUpSourcePlan] = useState(false);
  const [createTeamError, setCreateTeamError] = useState<string | null>(null);
  const [orgPlans, setOrgPlans] = useState<OrganisationTeamPlanOptionDTO[]>([]);
  const [isAddMembersOpen, setIsAddMembersOpen] = useState(false);
  const [selectedAddMemberIds, setSelectedAddMemberIds] = useState<Set<string>>(new Set());
  const [isUpdatingMembers, setIsUpdatingMembers] = useState(false);
  const [isReallocateOpen, setIsReallocateOpen] = useState(false);
  const [reallocatePlanId, setReallocatePlanId] = useState('');
  const [reallocateCreditsInput, setReallocateCreditsInput] = useState('');
  const [reallocateOverageToOrg, setReallocateOverageToOrg] = useState(true);
  const [reallocateAutoTopUpSourcePlan, setReallocateAutoTopUpSourcePlan] = useState(true);
  const [reallocateError, setReallocateError] = useState<string | null>(null);
  const [isUpdatingPlan, setIsUpdatingPlan] = useState(false);
  const [isDisbanding, setIsDisbanding] = useState(false);

  const sortedTeams = useMemo(
    () => [...teams].sort((a, b) => a.name.localeCompare(b.name)),
    [teams],
  );

  const teamsById = useMemo(() => new Map(sortedTeams.map(team => [team.id, team])), [sortedTeams]);
  const unassignedMembers = useMemo(() => getUnassignedMembers(members, sortedTeams), [members, sortedTeams]);

  const selectedTeam = selectedTeamId ? teamsById.get(selectedTeamId) ?? null : null;
  const teamMembers = useMemo(() => {
    const memberIds = selectedTeamDetails?.team.memberIds ?? selectedTeam?.memberIds ?? [];
    if (memberIds.length === 0) return [];
    const idSet = new Set(memberIds);
    return members.filter(member => idSet.has(member.id));
  }, [members, selectedTeam, selectedTeamDetails]);

  const fetchOrgPlans = useCallback(async () => {
    const currentKernel = kernel.current;
    if (!currentKernel) return;
    try {
      setOrgPlans(await currentKernel.getOrganisationTeamPlans(organisationId));
    } catch (error: any) {
      setOrgPlans([]);
      setCreateTeamError(getSpecificErrorMessage(error, t.failedLoadPlans));
    }
  }, [kernel, organisationId, t.failedLoadPlans]);

  const loadTeamDetails = useCallback(async (teamId: string) => {
    const currentKernel = kernel.current;
    if (!currentKernel) return;
    setIsLoadingTeamDetails(true);
    setTeamDetailsError(null);
    try {
      setSelectedTeamDetails(await currentKernel.getOrganisationTeamDetails(teamId));
    } catch (error: any) {
      setTeamDetailsError(getSpecificErrorMessage(error, t.failedLoadTeamDetails));
      setSelectedTeamDetails(null);
    } finally {
      setIsLoadingTeamDetails(false);
    }
  }, [kernel, t.failedLoadTeamDetails]);

  useEffect(() => {
    if (!selectedTeamId) {
      setSelectedTeamDetails(null);
      setTeamDetailsError(null);
      return;
    }
    void loadTeamDetails(selectedTeamId);
  }, [loadTeamDetails, selectedTeamId]);

  useEffect(() => {
    if (!selectedTeamId) return;
    if (teamsById.has(selectedTeamId)) return;
    setSelectedTeamId(null);
  }, [selectedTeamId, teamsById]);

  useEffect(() => {
    if (!isCreateOpen && !isReallocateOpen) return;
    void fetchOrgPlans();
  }, [fetchOrgPlans, isCreateOpen, isReallocateOpen]);

  const refreshAfterTeamMutation = useCallback(async () => {
    await onTeamsChanged();
    if (selectedTeamId) {
      await loadTeamDetails(selectedTeamId);
    }
  }, [loadTeamDetails, onTeamsChanged, selectedTeamId]);

  const resetCreateForm = useCallback(() => {
    setNameInput('');
    setDescriptionInput('');
    setSelectedMemberIds(new Set());
    setSelectedPlanId('');
    setReservedCreditsInput('');
    setAllowOverageToOrg(true);
    setCreateAutoTopUpSourcePlan(false);
    setCreateTeamError(null);
  }, []);

  const openCreate = useCallback(() => {
    resetCreateForm();
    setIsCreateOpen(true);
  }, [resetCreateForm]);

  const toggleCreateMember = useCallback((memberId: string) => {
    setSelectedMemberIds(prev => {
      const next = new Set(prev);
      if (next.has(memberId)) next.delete(memberId);
      else next.add(memberId);
      return next;
    });
  }, []);

  const toggleAddMember = useCallback((memberId: string) => {
    setSelectedAddMemberIds(prev => {
      const next = new Set(prev);
      if (next.has(memberId)) next.delete(memberId);
      else next.add(memberId);
      return next;
    });
  }, []);

  const handleCreateTeam = useCallback(async () => {
    const name = nameInput.trim();
    const reservedCreditsFloat = Number.parseFloat(reservedCreditsInput);
    if (!name) return;
    if (!selectedPlanId) {
      setCreateTeamError(t.planRequired);
      return;
    }
    const sourcePlanAvailable = orgPlans.find(plan => plan.id === selectedPlanId)?.availableCreditsFloat ?? 0;
    if (!canCreateTeamRule({
      name,
      sourcePlanId: selectedPlanId,
      credits: reservedCreditsFloat,
      sourcePlanAvailable,
      allowSourceTopUp: createAutoTopUpSourcePlan,
    })) {
      const topUpNeeded = getRequiredSourceTopUp(reservedCreditsFloat, sourcePlanAvailable);
      setCreateTeamError(topUpNeeded > 0 ? t.requiredSourceTopUp(formatCreditsInteger(topUpNeeded)) : t.reservedCreditsInvalid);
      return;
    }

    try {
      await kernel.current?.createOrganisationTeam({
        organisationId,
        name,
        description: descriptionInput.trim() || null,
        memberIds: Array.from(selectedMemberIds),
        sourceOrganisationPlanId: selectedPlanId,
        reservedCreditsFloat,
        allowOverageToOrg,
        autoTopUpSourcePlan: createAutoTopUpSourcePlan,
      });
      setIsCreateOpen(false);
      resetCreateForm();
      await onTeamsChanged();
      toast.success(t.teamCreated);
    } catch (error: any) {
      setCreateTeamError(getSpecificErrorMessage(error, t.failedCreateTeam));
    }
  }, [allowOverageToOrg, createAutoTopUpSourcePlan, descriptionInput, kernel, nameInput, onTeamsChanged, orgPlans, organisationId, reservedCreditsInput, resetCreateForm, selectedMemberIds, selectedPlanId, t]);

  const handleOpenAddMembers = useCallback(() => {
    setSelectedAddMemberIds(new Set());
    setIsAddMembersOpen(true);
  }, []);

  const handleAddMembers = useCallback(async () => {
    if (!selectedTeamId) return;
    const memberIds = Array.from(selectedAddMemberIds);
    if (memberIds.length === 0) {
      setIsAddMembersOpen(false);
      return;
    }
    setIsUpdatingMembers(true);
    try {
      await kernel.current?.addOrganisationTeamMembers(selectedTeamId, memberIds);
      setIsAddMembersOpen(false);
      setSelectedAddMemberIds(new Set());
      await refreshAfterTeamMutation();
      toast.success(t.membersAdded);
    } catch (error: any) {
      setTeamDetailsError(getSpecificErrorMessage(error, t.failedAddMembers));
    } finally {
      setIsUpdatingMembers(false);
    }
  }, [kernel, refreshAfterTeamMutation, selectedAddMemberIds, selectedTeamId, t.failedAddMembers, t.membersAdded]);

  const handleRemoveMember = useCallback(async (teamId: string, memberId: string) => {
    if (!window.confirm(t.removeMemberConfirm)) return;
    setIsUpdatingMembers(true);
    try {
      await kernel.current?.removeOrganisationTeamMember(teamId, memberId);
      await refreshAfterTeamMutation();
      toast.success(t.memberRemoved);
    } catch (error: any) {
      setTeamDetailsError(getSpecificErrorMessage(error, t.failedRemoveMember));
    } finally {
      setIsUpdatingMembers(false);
    }
  }, [kernel, refreshAfterTeamMutation, t.failedRemoveMember, t.memberRemoved, t.removeMemberConfirm]);

  const handleOpenReallocate = useCallback(() => {
    if (!selectedTeamDetails) return;
    setReallocatePlanId(selectedTeamDetails.team.teamPlan?.sourceOrganisationPlanId ?? '');
    setReallocateCreditsInput(
      selectedTeamDetails.team.teamPlan ? String(selectedTeamDetails.team.teamPlan.reservedCreditsFloat) : '',
    );
    setReallocateOverageToOrg(selectedTeamDetails.team.teamPlan?.allowOverageToOrg ?? true);
    setReallocateAutoTopUpSourcePlan(true);
    setReallocateError(null);
    setIsReallocateOpen(true);
  }, [selectedTeamDetails]);

  const handleReallocatePlan = useCallback(async () => {
    if (!selectedTeamDetails) return;
    const reservedCreditsFloat = Number.parseFloat(reallocateCreditsInput);
    if (!reallocatePlanId) {
      setReallocateError(t.planRequired);
      return;
    }
    const pool = readTeamCreditPool(selectedTeamDetails.team);
    const maxReservable = getMaxReservableCredits({
      sourcePlanAvailable: orgPlans.find(plan => plan.id === reallocatePlanId)?.availableCreditsFloat ?? 0,
      currentReserved: pool.reserved,
      isSameSourcePlan: reallocatePlanId === pool.sourcePlanId,
    });
    if (!canSetTeamReservedCredits({
      requestedCredits: reservedCreditsFloat,
      pool,
      maxReservable,
      allowSourceTopUp: reallocateAutoTopUpSourcePlan,
    })) {
      const topUpNeeded = getRequiredSourceTopUp(reservedCreditsFloat, maxReservable);
      setReallocateError(topUpNeeded > 0 ? t.requiredSourceTopUp(formatCreditsInteger(topUpNeeded)) : t.reservedCreditsInvalid);
      return;
    }
    setIsUpdatingPlan(true);
    try {
      await kernel.current?.updateOrganisationTeamPlan(selectedTeamDetails.team.id, {
        sourceOrganisationPlanId: reallocatePlanId,
        reservedCreditsFloat,
        allowOverageToOrg: reallocateOverageToOrg,
        autoTopUpSourcePlan: reallocateAutoTopUpSourcePlan,
      });
      setIsReallocateOpen(false);
      setReallocateError(null);
      await refreshAfterTeamMutation();
      toast.success(t.teamBudgetUpdated);
    } catch (error: any) {
      setReallocateError(getSpecificErrorMessage(error, t.failedUpdateTeamBudget));
    } finally {
      setIsUpdatingPlan(false);
    }
  }, [kernel, orgPlans, reallocateAutoTopUpSourcePlan, reallocateCreditsInput, reallocateOverageToOrg, reallocatePlanId, refreshAfterTeamMutation, selectedTeamDetails, t]);

  const handleDisbandTeam = useCallback(async () => {
    if (!selectedTeamId) return;
    if (!window.confirm(t.disbandConfirm)) return;
    setIsDisbanding(true);
    try {
      await kernel.current?.deleteOrganisationTeam(selectedTeamId);
      setSelectedTeamId(null);
      setSelectedTeamDetails(null);
      await onTeamsChanged();
      toast.success(t.teamDisbanded);
    } catch (error: any) {
      setTeamDetailsError(getSpecificErrorMessage(error, t.failedDisbandTeam));
    } finally {
      setIsDisbanding(false);
    }
  }, [kernel, onTeamsChanged, selectedTeamId, t.disbandConfirm, t.failedDisbandTeam, t.teamDisbanded]);

  const openMemberContextMenu = useCallback((
    member: MemberData,
    event: React.MouseEvent,
    team: OrganisationTeamDTO | null,
  ) => {
    const baseActions = buildMemberContextActions?.(member) ?? [];
    const actions = [...baseActions];
    if (team && canManageTeams) {
      actions.push({
        label: t.removeFromTeam,
        icon: <UserMinus size={14} />,
        separatorBefore: actions.length > 0,
        onClick: () => handleRemoveMember(team.id, member.id),
      });
    }
    if (actions.length === 0) return;
    event.preventDefault();
    event.stopPropagation();
    kernel.current?.setMenuData({ position: { x: event.clientX, y: event.clientY }, actions });
  }, [buildMemberContextActions, canManageTeams, handleRemoveMember, kernel, t.removeFromTeam]);

  const renderTeamGroup = useCallback((team: OrganisationTeamDTO | null, groupMembers: MemberData[]) => {
    const remaining = team ? getTeamRemainingCredits(team) : null;
    return (
      <div key={team?.id ?? 'unassigned'} className="py-1">
        <div className="flex min-h-10 items-center justify-between gap-4 pb-1">
          {team ? (
            <button
              type="button"
              onClick={() => setSelectedTeamId(team.id)}
              className="!text-[15px] text-[#8E8E8E] transition-colors hover:text-[#ECECEC]"
            >
              {team.name}
            </button>
          ) : (
            <span className="text-[#8E8E8E]" style={{ fontSize: 15 }}>{t.ungrouped}</span>
          )}
          {team ? <span className="tabular-nums text-[#8E8E8E]" style={{ fontSize: 14 }}>{t.creditsLeft(formatCreditsInteger(remaining ?? 0))}</span> : null}
        </div>
        <MembersList
          members={groupMembers}
          avatarTimestamp={avatarTimestamp}
          isViewerAdmin={isViewerAdmin}
          currentUserId={currentUserId}
          canOpenContextMenuFor={canOpenContextMenuFor}
          onMemberContextMenu={(member, event) => openMemberContextMenu(member, event, team)}
          emptyMessage={team ? t.noMembersInTeam : t.noUngroupedMembers}
        />
      </div>
    );
  }, [avatarTimestamp, canOpenContextMenuFor, currentUserId, isViewerAdmin, openMemberContextMenu, t]);

  if (selectedTeamId) {
    return (
      <section className="mb-6">
        <button type="button" onClick={() => setSelectedTeamId(null)} className="mb-4 !text-[15px] text-[#8E8E8E] transition-colors hover:text-[#ECECEC]">
          {t.backToOrgMembers}
        </button>
        <div>
          {teamDetailsError && (
            <div className="mb-4 rounded-[12px] px-0 py-2 text-[14px] text-[#F28B82]">
              {teamDetailsError}
            </div>
          )}
          {isLoadingTeamDetails ? (
            <div className="flex h-48 items-center justify-center">
              <SimpleLoader size={48} message={t.loadingTeamDetails} />
            </div>
          ) : selectedTeamDetails ? (
            <div className="space-y-7">
              <div className="flex flex-wrap items-start justify-between gap-4 border-b border-white/[0.08] pb-3">
                <div>
                  <h2 className="text-[28px] font-normal leading-9 text-white">{selectedTeamDetails.team.name}</h2>
                  {selectedTeamDetails.team.description && (
                    <p className="mt-1 text-[12px] leading-[18px] text-white/45">{selectedTeamDetails.team.description}</p>
                  )}
                </div>
                <div className="text-right text-[11px] leading-5 text-white/35">
                  <div>{t.membersCount(selectedTeamDetails.stats.totalMembers)}</div>
                  <div>{t.creditsLeft(formatCreditsInteger(getTeamRemainingCredits(selectedTeamDetails.team)))}</div>
                </div>
              </div>

              <div className="grid gap-7 md:grid-cols-2">
                <div>
                  <h3 className="mb-2 text-[17px] font-medium text-[#ECECEC]">{t.credits}</h3>
                  <div className="space-y-1 text-[12px]">
                    <div className="flex min-h-7 items-center justify-between"><span className="text-white/50">{t.reserved}</span><span className="font-mono text-white/70">{formatCreditsInteger(selectedTeamDetails.team.teamPlan?.reservedCreditsFloat ?? 0)}</span></div>
                    <div className="flex min-h-7 items-center justify-between"><span className="text-white/50">{t.usedCredits}</span><span className="font-mono text-white/70">{formatCreditsInteger(selectedTeamDetails.team.teamPlan?.usedCreditsFloat ?? 0)}</span></div>
                    <div className="flex min-h-7 items-center justify-between"><span className="text-white/50">{t.remaining}</span><span className="font-mono text-white/70">{formatCreditsInteger(getTeamRemainingCredits(selectedTeamDetails.team))}</span></div>
                    <div className="flex min-h-7 items-center justify-between"><span className="text-white/50">{t.last30Days}</span><span className="font-mono text-white/70">{formatCreditsInteger(selectedTeamDetails.stats.creditsLast30DaysFloat)}</span></div>
                    <div className="flex min-h-7 items-center justify-between"><span className="text-white/50">{t.expires}</span><span className="text-white/65">{selectedTeamDetails.team.teamPlan?.expirationDate ? new Date(selectedTeamDetails.team.teamPlan.expirationDate).toLocaleDateString() : 'No expiry'}</span></div>
                  </div>
                </div>
                <div>
                  <h3 className="mb-2 text-[17px] font-medium text-[#ECECEC]">{t.stats}</h3>
                  <div className="space-y-1 text-[12px]">
                    <div className="flex min-h-7 items-center justify-between"><span className="text-white/50">{t.projects}</span><span className="font-mono text-white/70">{selectedTeamDetails.stats.totalProjects.toLocaleString()}</span></div>
                    <div className="flex min-h-7 items-center justify-between"><span className="text-white/50">{t.generations}</span><span className="font-mono text-white/70">{selectedTeamDetails.stats.totalGenerations.toLocaleString()}</span></div>
                    <div className="flex min-h-7 items-center justify-between"><span className="text-white/50">{t.totalCreditsUsed}</span><span className="font-mono text-white/70">{formatCreditsInteger(selectedTeamDetails.stats.totalCreditsSpentFloat)}</span></div>
                  </div>
                </div>
              </div>

              <TeamConsumptionTimeline teamId={selectedTeamDetails.team.id} teamName={selectedTeamDetails.team.name} />

              <div>
                <div className="mb-1 flex min-h-9 items-center justify-between gap-3 border-b border-white/[0.08] pb-2.5">
                  <h3 className="text-[17px] font-medium text-[#ECECEC]">{t.members}</h3>
                  <button type="button" onClick={handleOpenAddMembers} disabled={!canManageTeams || unassignedMembers.length === 0} className={buttonClass}>
                    <UserPlus size={14} />
                    {t.addMembers}
                  </button>
                </div>
                <MembersList
                  members={teamMembers}
                  avatarTimestamp={avatarTimestamp}
                  isViewerAdmin={isViewerAdmin}
                  currentUserId={currentUserId}
                  canOpenContextMenuFor={canOpenContextMenuFor}
                  onMemberContextMenu={(member, event) => openMemberContextMenu(member, event, selectedTeamDetails.team)}
                  emptyMessage={t.noMembersInTeam}
                />
              </div>

              <div className="flex flex-wrap justify-end gap-3">
                <button type="button" onClick={handleOpenReallocate} disabled={!canManageTeams} className={buttonClass}>
                  {t.teamBudget}
                </button>
                <button
                  type="button"
                  onClick={handleDisbandTeam}
                  disabled={!canManageTeams || isDisbanding}
                  className="inline-flex h-7 items-center justify-center gap-1.5 rounded-md border border-[#FF7369]/20 bg-[#FF7369]/10 px-2.5 text-[12px] font-medium text-[#FF7369] transition-colors hover:bg-[#FF7369]/15 disabled:cursor-not-allowed disabled:opacity-45"
                >
                  <Trash2 size={14} />
                  {t.disbandTeam}
                </button>
              </div>
            </div>
          ) : (
            <div className="text-sm text-white/45">{t.teamDetailsUnavailable}</div>
          )}
        </div>

        {isAddMembersOpen && selectedTeamDetails && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center bg-[#141414]/90 p-[2vmin] backdrop-blur-sm">
            <div className="max-h-[85vh] w-[min(680px,94vw)] overflow-y-auto rounded-[24px] border border-white/[0.06] bg-[#212121] p-[clamp(18px,2.5vmin,28px)] shadow-xl">
              <div className="mb-5 flex items-center justify-between">
                <h3 className="text-[18px] font-medium text-[#ECECEC]">{t.addMembersTitle}</h3>
                <button type="button" onClick={() => setIsAddMembersOpen(false)} className="text-sm text-white/45 hover:text-white">{t.cancel}</button>
              </div>
              <TeamMembersPicker
                legend={t.members}
                members={unassignedMembers}
                selectedMemberIds={selectedAddMemberIds}
                onToggleMemberId={toggleAddMember}
                emptyMessage={t.noMembersAvailable}
              />
              <div className="mt-5 flex justify-end gap-2">
                <button type="button" onClick={() => setIsAddMembersOpen(false)} className={buttonClass}>{t.cancel}</button>
                <button type="button" onClick={handleAddMembers} disabled={!canManageTeams || selectedAddMemberIds.size === 0 || isUpdatingMembers} className={primaryButtonClass}>{t.addSelected}</button>
              </div>
            </div>
          </div>
        )}

        {isReallocateOpen && selectedTeamDetails && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center bg-[#141414]/90 p-[2vmin] backdrop-blur-sm">
            <div className="max-h-[85vh] w-[min(680px,94vw)] overflow-y-auto rounded-[24px] border border-white/[0.06] bg-[#212121] p-[clamp(18px,2.5vmin,28px)] shadow-xl">
              <div className="mb-5 flex items-center justify-between">
                <h3 className="text-[18px] font-medium text-[#ECECEC]">{t.teamBudget}</h3>
                <button type="button" onClick={() => setIsReallocateOpen(false)} className="text-sm text-white/45 hover:text-white">{t.cancel}</button>
              </div>
              {reallocateError && (
                <div className="mb-4 rounded-[12px] px-0 py-2 text-[14px] text-[#F28B82]">
                  {reallocateError}
                </div>
              )}
              <TeamPlanFieldset
                legend={t.teamBudget}
                t={t}
                selectId="reallocate-team-plan"
                creditsInputId="reallocate-team-credits"
                overageId="reallocate-team-overage"
                planId={reallocatePlanId}
                onPlanChange={setReallocatePlanId}
                creditsInput={reallocateCreditsInput}
                onCreditsChange={setReallocateCreditsInput}
                allowOverage={reallocateOverageToOrg}
                onAllowOverageChange={setReallocateOverageToOrg}
                autoTopUpSourcePlan={reallocateAutoTopUpSourcePlan}
                onAutoTopUpSourcePlanChange={setReallocateAutoTopUpSourcePlan}
                plans={orgPlans}
                currentTeamSourcePlanId={selectedTeamDetails.team.teamPlan?.sourceOrganisationPlanId ?? null}
                currentTeamReservedCredits={selectedTeamDetails.team.teamPlan?.reservedCreditsFloat ?? null}
                currentTeamUsedCredits={selectedTeamDetails.team.teamPlan?.usedCreditsFloat ?? null}
              />
              <div className="mt-5 flex justify-end gap-2">
                <button type="button" onClick={() => setIsReallocateOpen(false)} className={buttonClass}>{t.cancel}</button>
                <button type="button" onClick={handleReallocatePlan} disabled={!canManageTeams || !reallocatePlanId || !reallocateCreditsInput.trim() || isUpdatingPlan} className={primaryButtonClass}>{t.saveChanges}</button>
              </div>
            </div>
          </div>
        )}
      </section>
    );
  }

  return (
    <section className="mb-6">
      {/* No 'Members' heading: the page's own title already says it (owner's ChatGPT pass, 2026-09-29). */}
      <div className="mb-2 flex min-h-10 flex-wrap items-center justify-end gap-3 pb-2">
        <h2 className="sr-only">{t.members}</h2>
        {isViewerAdmin && (
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={onAddMember} className={primaryButtonClass}>
              <UserPlus size={14} /> {t.addMember}
            </button>
            <button type="button" onClick={openCreate} disabled={!canManageTeams} className={buttonClass}>
              <Users size={14} /> {t.addTeam}
            </button>
          </div>
        )}
      </div>

      <div className="space-y-6">
        {sortedTeams.map(team => {
          const idSet = new Set(team.memberIds);
          const groupMembers = members.filter(member => idSet.has(member.id));
          return renderTeamGroup(team, groupMembers);
        })}
        {renderTeamGroup(null, unassignedMembers)}
      </div>

      {isCreateOpen && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-[#141414]/90 p-[2vmin] backdrop-blur-sm">
          <div className="max-h-[88vh] w-[min(680px,94vw)] overflow-y-auto rounded-[24px] border border-white/[0.06] bg-[#212121] p-[clamp(18px,2.5vmin,28px)] shadow-xl">
            <div className="mb-5 flex items-center justify-between">
              <h3 className="text-[18px] font-medium text-[#ECECEC]">{t.createTeam}</h3>
              <button type="button" onClick={() => setIsCreateOpen(false)} className="text-sm text-white/45 hover:text-white">{t.cancel}</button>
            </div>
            <div className="space-y-6">
              {createTeamError && (
                <div className="rounded-[12px] px-0 py-2 text-[14px] text-[#F28B82]">
                  {createTeamError}
                </div>
              )}
              <fieldset className="space-y-3">
                <legend className="mb-2 text-[17px] font-medium text-[#ECECEC]">{t.team}</legend>
                <div>
                  <label htmlFor="org-team-name" className="mb-1 block text-sm text-white/70">{t.teamName}</label>
                  <input id="org-team-name" value={nameInput} onChange={(event) => setNameInput(event.target.value)} className={inputClass} placeholder={t.teamName} />
                </div>
                <div>
                  <label htmlFor="org-team-description" className="mb-1 block text-sm text-white/70">{t.description}</label>
                  <textarea id="org-team-description" value={descriptionInput} onChange={(event) => setDescriptionInput(event.target.value)} className={`${inputClass} h-auto min-h-[88px] py-2`} placeholder={t.description} rows={3} />
                </div>
              </fieldset>
              <TeamPlanFieldset
                legend={t.teamBudget}
                t={t}
                selectId="org-team-plan"
                creditsInputId="org-team-credits"
                overageId="org-team-overage"
                planId={selectedPlanId}
                onPlanChange={setSelectedPlanId}
                creditsInput={reservedCreditsInput}
                onCreditsChange={setReservedCreditsInput}
                allowOverage={allowOverageToOrg}
                onAllowOverageChange={setAllowOverageToOrg}
                autoTopUpSourcePlan={createAutoTopUpSourcePlan}
                onAutoTopUpSourcePlanChange={setCreateAutoTopUpSourcePlan}
                plans={orgPlans}
              />
              <TeamMembersPicker
                legend={t.initialMembers}
                members={unassignedMembers}
                selectedMemberIds={selectedMemberIds}
                onToggleMemberId={toggleCreateMember}
                emptyMessage={t.noMembersAvailable}
              />
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" onClick={() => setIsCreateOpen(false)} className={buttonClass}>{t.cancel}</button>
              <button type="button" onClick={handleCreateTeam} disabled={!canManageTeams || !nameInput.trim() || !selectedPlanId || !reservedCreditsInput.trim()} className={primaryButtonClass}>{t.create}</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
});
TeamManagementSection.displayName = 'TeamManagementSection';
